import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Layers, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CatalogHome() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xl">Loading catalog...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero Banner */}
        <section className="bg-gradient-to-br from-primary/10 to-secondary/10 border-b">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
            <div className="max-w-3xl">
              <div className="inline-block mb-4">
                <span className="text-xs font-bold tracking-widest uppercase text-primary px-4 py-2 bg-primary/10 rounded-sm border border-primary/30">
                  Product Catalog
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }} data-testid="catalog-heading">
                Browse by Category
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Explore our comprehensive range of retail display solutions. Select a category to view products, specifications, and pricing.
              </p>
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {categories.length === 0 ? (
              <div className="text-center py-16">
                <Layers className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Categories Available</h3>
                <p className="text-muted-foreground">Categories will appear here once added by the admin.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="categories-grid">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/catalog/${category.id}`}
                    className="category-card group"
                    data-testid={`category-card-${category.slug}`}
                  >
                    <div className="relative h-64 rounded-sm overflow-hidden border border-border/50 bg-gray-100">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{category.name}</h3>
                        <p className="text-sm text-white/90 mb-4 line-clamp-2">{category.description}</p>
                        <div className="flex items-center gap-2 text-sm font-medium">
                          View Products
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}